import {testIdProps} from '@github-ui/test-id-props'
import {Checkbox, Link} from '@primer/react'
import {memo, useCallback, useRef} from 'react'

import type {RepositoryItem, SuggestedIssue, SuggestedPullRequest} from '../../api/repository/contracts'
import useIsVisible from '../board/hooks/use-is-visible'
import {SanitizedHtml} from '../dom/sanitized-html'
import {ItemState} from '../item-state'
import {useBulkAddItems} from './bulk-add/bulk-add-items-provider'
import styles from './suggested-item.module.css'

type SuggestedItemProps = {
  item: RepositoryItem
  keyName: string
  url: string
}

export const SuggestedItem = memo<SuggestedItemProps>(function SuggestedItem({item, keyName, url}) {
  const ref = useRef(null)
  const {isVisible} = useIsVisible({ref})

  return (
    <li key={keyName} ref={ref} className={styles.Box}>
      {isVisible ? <SuggestedItemInner keyName={keyName} item={item} url={url} /> : null}
    </li>
  )
})

const SuggestedItemInner = memo<SuggestedItemProps>(function SuggestedItemInner({item, url}) {
  const {selectItem, isSelected} = useBulkAddItems()

  const onCheck = useCallback(() => {
    selectItem(item)
  }, [selectItem, item])

  const itemTitleId = `item-title-${item.number}`

  return (
    <>
      <label className={styles.Box_1} {...testIdProps('repo-item')}>
        <Checkbox
          aria-labelledby={itemTitleId}
          checked={isSelected(item.number)}
          onChange={onCheck}
          className={styles.Checkbox}
        />
      </label>
      <div className={styles.Box_2}>
        <ItemState
          type={item.type}
          state={item.state}
          stateReason={(item as SuggestedIssue)?.stateReason}
          isDraft={!!(item as SuggestedPullRequest).isDraft}
          isBlocked={false}
        />
      </div>
      <div className={styles.Box_3}>
        <span id={itemTitleId} className={styles.Box_4}>
          <Link target="_blank" rel="noreferrer" href={url} tabIndex={-1} className={styles.Link}>
            <SanitizedHtml>{item.titleHtml}</SanitizedHtml>
          </Link>
        </span>
        <span className={styles.Text}>{`#${item.number}`}</span>
      </div>
    </>
  )
})

try{ SuggestedItem.displayName ||= 'SuggestedItem' } catch {}
try{ SuggestedItemInner.displayName ||= 'SuggestedItemInner' } catch {}